import React, { useMemo } from 'react';
import { Trash2, Mail, Rocket } from 'lucide-react';
import { fmtDateTime } from './constants';

const STATUS_COLORS = {
  waiting: 'bg-[#c9a961]/15 text-[#8a7132] border-[#c9a961]/40',
  notified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-100 text-slate-700 border-slate-300',
};

const StatusBadge = ({ value }) => (
  <span className={`inline-block text-[10px] tracking-[0.2em] uppercase font-semibold px-2.5 py-1 border ${STATUS_COLORS[value] || STATUS_COLORS.waiting}`}>
    {value}
  </span>
);

const WaitlistTab = ({ entries, onUpdateStatus, onDelete, onOpenLaunch, launchMessage }) => {
  // Group entries by service, count only "waiting" per service.
  const services = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const s = map.get(e.service) || { total: 0, waiting: 0, notified: 0 };
      s.total += 1;
      if (e.status === 'waiting') s.waiting += 1;
      if (e.status === 'notified') s.notified += 1;
      map.set(e.service, s);
    });
    return Array.from(map.entries()).map(([name, counts]) => ({ name, ...counts }));
  }, [entries]);

  return (
    <div className="space-y-6" data-testid="waitlist-tab">
      {/* Launch controls */}
      <div className="bg-white border border-black/5 p-6">
        <div className="flex items-center gap-2 mb-3">
          <Rocket size={18} className="text-[#c9a961]" strokeWidth={1.5} />
          <h3
            className="font-serif text-xl text-[#1c1e20]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Send a launch announcement
          </h3>
        </div>
        <p className="text-[13px] text-[#6b6b6b] mb-5">
          When a service goes live, email every waiting sign-up in one click.
          Everyone who receives the email is automatically marked <em>notified</em>.
        </p>

        {services.length === 0 ? (
          <p className="text-[13px] text-[#6b6b6b] italic">No waitlist sign-ups yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((s) => (
              <div
                key={s.name}
                data-testid={`launch-card-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="border border-black/10 p-5 flex flex-col justify-between gap-4"
              >
                <div>
                  <h4
                    className="font-serif text-lg text-[#1c1e20]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {s.name}
                  </h4>
                  <p className="text-[12px] text-[#6b6b6b] mt-1">
                    <span className="text-[#8a7132] font-semibold">{s.waiting}</span> waiting
                    {' · '}
                    <span className="text-emerald-700 font-semibold">{s.notified}</span> already notified
                    {' · '}
                    {s.total} total
                  </p>
                </div>
                <button
                  onClick={() => onOpenLaunch(s.name, s.waiting)}
                  disabled={s.waiting === 0}
                  data-testid={`launch-open-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                  className="self-start flex items-center gap-2 bg-[#c9a961] hover:bg-[#b89751] text-[#141618] tracking-[0.22em] text-[11px] font-semibold px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Rocket size={13} /> SEND LAUNCH ANNOUNCEMENT
                </button>
              </div>
            ))}
          </div>
        )}

        {launchMessage && (
          <p className="text-[12px] text-[#8a7132] mt-4" data-testid="launch-result-message">{launchMessage}</p>
        )}
      </div>

      {/* Sign-ups table */}
      <div className="bg-white border border-black/5 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.22em] text-[#6b6b6b] border-b border-black/10">
              <th className="px-4 py-3 font-semibold">SERVICE</th>
              <th className="px-4 py-3 font-semibold">NAME</th>
              <th className="px-4 py-3 font-semibold">EMAIL</th>
              <th className="px-4 py-3 font-semibold">NOTE</th>
              <th className="px-4 py-3 font-semibold">SIGNED UP</th>
              <th className="px-4 py-3 font-semibold">STATUS</th>
              <th className="px-4 py-3 font-semibold text-right">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-[#6b6b6b] py-10">No waitlist sign-ups yet.</td>
              </tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-black/5 hover:bg-[#faf6f0]/60" data-testid={`waitlist-row-${e.id}`}>
                <td className="px-4 py-3 font-semibold text-[#1c1e20]">{e.service}</td>
                <td className="px-4 py-3 text-[#3c3c3c]">{e.name || '—'}</td>
                <td className="px-4 py-3 text-[#3c3c3c]">
                  <a href={`mailto:${e.email}`} className="hover:text-[#c9a961] inline-flex items-center gap-1.5">
                    <Mail size={12} /> {e.email}
                  </a>
                </td>
                <td className="px-4 py-3 text-[#6b6b6b] text-[12px] max-w-[260px]">
                  <span className="line-clamp-2">{e.note || '—'}</span>
                </td>
                <td className="px-4 py-3 text-[#6b6b6b] text-[12px]">{fmtDateTime(e.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1.5">
                    <StatusBadge value={e.status} />
                    <select
                      value={e.status}
                      onChange={(ev) => onUpdateStatus(e.id, ev.target.value)}
                      data-testid={`waitlist-status-select-${e.id}`}
                      className="text-[12px] border border-black/15 px-2 py-1 bg-white focus:outline-none focus:border-[#c9a961]"
                    >
                      <option value="waiting">Waiting</option>
                      <option value="notified">Notified</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onDelete(e.id)}
                    data-testid={`waitlist-delete-${e.id}`}
                    className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-[12px] font-semibold"
                  >
                    <Trash2 size={13} /> REMOVE
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WaitlistTab;
